import Column from 'components/Column'
import { AutoRow } from 'components/Row'
import { useConnection } from 'hooks/useConnection'
import ShareModal from 'pages/Claim/ShareModal'
import { useMemo, useState } from 'react'
import { CheckCircle, Triangle } from 'react-feather'
import { FaShare } from 'react-icons/fa'
import { TransactionType } from 'state/transactions/types'
import styled from 'styled-components'
import { shortenAddress } from 'utils'
import { formatEther } from 'viem'

import { useAllTransactions } from '../../state/transactions/hooks'
import { ExternalLink } from '../../theme'
import { ExplorerDataType, getExplorerLink } from '../../utils/getExplorerLink'
import Loader from '../Loader'
import { TransactionSummary } from './TransactionSummary'

const MainContainer = styled.div`
  width: 100%;
  padding: 0.25rem;
  background: ${({ theme }) => theme.cardSecondary};
  border-radius: 4px;
`

const TransactionStatusText = styled.div`
  display: flex;
  align-items: center;
  :hover {
    text-decoration: underline;
  }
`

const TransactionState = styled(ExternalLink)<{ pending: boolean; success?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none !important;
  border-radius: 0.5rem;
  padding: 0.25rem 0rem;
  font-weight: 500;
  font-size: 0.825rem;
  color: ${({ theme }) => theme.deprecated_primary1};
`

const RecipientInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 4px;
`

const IconWrapper = styled.div<{ pending: boolean; success?: boolean }>`
  color: ${({ pending, success, theme }) =>
    pending ? theme.deprecated_primary1 : success ? theme.deprecated_green1 : theme.deprecated_red1};
`

const MigrateTransactionButton = styled.button`
  cursor: pointer;
  :hover {
    opacity: 0.8;
  }

  color: ${({ theme }) => theme.buttonDisable};
`

export function MigrateTransactionShareButton({ amount }: { amount: string }) {
  const [isOpen, setIsOpen] = useState(false)

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault()
    setIsOpen(true)
  }

  return (
    <>
      <MigrateTransactionButton onClick={handleClick} title="Share on Twitter">
        <FaShare size={16} color="#efefefe" />
      </MigrateTransactionButton>

      <ShareModal isOpen={isOpen} onDismiss={() => setIsOpen(false)} amount={amount} />
    </>
  )
}

export default function Transaction({ hash }: { hash: string }) {
  const { chainId, account } = useConnection()
  const allTransactions = useAllTransactions()

  const tx = allTransactions?.[hash]
  const info = tx?.info
  const pending = !tx?.receipt
  const success = !pending && tx && (tx.receipt?.status === 1 || typeof tx.receipt?.status === 'undefined')

  const isSwap = info?.type === TransactionType.SWAP
  const isCustomRecipient = useMemo(() => {
    if (!isSwap || !info) return false

    if (info.type === TransactionType.SWAP && 'recipient' in info) {
      return info.isCustomRecipient && info.recipient !== account
    }

    return false
  }, [isSwap, info, account])

  const recipientAddress = useMemo(() => {
    if (!isSwap || !info) return null

    if (info.type === TransactionType.SWAP && 'recipient' in info) {
      return info.recipient
    }

    return null
  }, [isSwap, info])

  if (!chainId) return null

  return (
    <MainContainer>
      <TransactionState
        href={getExplorerLink(chainId, hash, ExplorerDataType.TRANSACTION)}
        pending={pending}
        success={success}
      >
        <Column>
          <TransactionStatusText>
            <TransactionSummary info={info} /> ↗
          </TransactionStatusText>
          {isCustomRecipient && recipientAddress && (
            <RecipientInfo>to: {shortenAddress(recipientAddress)} (External wallet)</RecipientInfo>
          )}
        </Column>
        <AutoRow width="auto" gap="2px" justify="center">
          {info.type === TransactionType.TOKEN_MIGRATION && (
            <MigrateTransactionShareButton amount={formatEther(BigInt(info.amount))} />
          )}
          <IconWrapper pending={pending} success={success}>
            {pending ? <Loader /> : success ? <CheckCircle size="16" /> : <Triangle size="16" />}
          </IconWrapper>
        </AutoRow>
      </TransactionState>
    </MainContainer>
  )
}
